import { useCallback } from 'react';

import { useApi } from '@backstage/core-plugin-api';

import {
  type ElementPath,
  trackingApiRef,
  usePageView,
} from '@spotify/plugin-confidence-tracking-react';

export type IssuePropertyTrackingName =
  | 'assignee'
  | 'autonomy-level'
  | 'kind'
  | 'priority'
  | 'state'
  | 'surfaces';

export type IssuePropertyTrackingContext = 'issue-details';

export const useTrackIssuePropertyUpdate = (
  issueName: string,
  options: { context?: IssuePropertyTrackingContext } = {},
): ((property: IssuePropertyTrackingName, value: string) => void) => {
  const tracking = useApi(trackingApiRef);
  const { pageId, pageViewId } = usePageView();
  const { context } = options;

  return useCallback(
    (property: IssuePropertyTrackingName, value: string): void => {
      const elementPath: ElementPath = [
        ...(pageId ? [{ role: 'main' as const, name: pageId }] : []),
        ...(context ? [{ role: 'region' as const, name: context }] : []),
        { role: 'group', name: 'issue-properties' },
        { role: 'option', name: property },
      ];

      tracking.trackInteraction({
        elementPath,
        type: 'click',
        action: 'update',
        data: {
          ...(pageId ? { pageId } : {}),
          ...(pageViewId ? { pageViewId } : {}),
          issue: issueName,
          property,
          value,
        },
      });
    },
    [context, issueName, pageId, pageViewId, tracking],
  );
};
